import { m } from 'framer-motion';
import * as Sentry from '@sentry/react';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { usePopover } from 'minimal-shared/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { transitionTap, varHover, varTap } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import { CustomPopover } from 'src/components/custom-popover';

import { localStorageGetItem, AUTH_STORAGE_KEYS } from 'src/utils/storage-available';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const user = JSON.parse(localStorageGetItem(AUTH_STORAGE_KEYS.user) || '{}');

  const { logout } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  // NAT-2804: logout itself ends in a full navigation to the login page. A router navigation here
  // would race it, so the menu only closes.
  const handleLogout = async () => {
    popover.onClose();
    try {
      await logout();
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={popover.open}
      anchorEl={popover.anchorEl}
      onClose={popover.onClose}
      slotProps={{ paper: { sx: { p: 0, width: 200 } }, arrow: { offset: 20 } }}
    >
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography variant="subtitle2" noWrap>
          {user?.fullName}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {user?.email}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      {/* v9 MenuItems require MenuListContext; CustomPopover does not provide it. */}
      <MenuList disablePadding>
        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </MenuList>
    </CustomPopover>
  );

  const renderActionButton = () => (
    <IconButton
      component={m.button}
      whileTap={varTap(0.96)}
      whileHover={varHover(1.04)}
      transition={transitionTap()}
      aria-label="Account button"
      onClick={popover.onOpen}
      sx={{
        width: 40,
        height: 40,
        background: (theme) => alpha(theme.palette.grey[500], 0.08),
        ...(popover.open && {
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        }),
      }}
    >
      <Avatar
        src=""
        alt={user?.fullName}
        sx={{
          width: 36,
          height: 36,
          border: (theme) => `solid 2px ${theme.palette.background.default}`,
        }}
      >
        {user?.fullName?.charAt(0)?.toUpperCase()}
      </Avatar>
    </IconButton>
  );

  return (
    <>
      {renderActionButton()}
      {renderMenuActions()}
    </>
  );
}
